// Verkaufen an der Kasse: der laufende Bon lebt als Entwurf auf dem Server (übersteht Neuladen und Gerätewechsel),
// jede Änderung ist ein Schritt (`ops`), gerechnet wird im Belegkern. Bezahlen schreibt den Bon fest, bucht den
// Bestand am Standort der Kasse, die Zahlungen in die Schicht und den Demo-Signatursatz („DEMO – keine TSE“).
// Diese Aktionen bedient nur die Kasse selbst – der Assistent bekommt sie nicht (assistant: 'never').
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { VOUCHER_MAX_CENTS, VOUCHER_MIN_CENTS } from '../lib/labels'
import { zTillEcho, zTillProof } from '../lib/offline/types'
import { zAmountCentsOrZero, zDiscountBp, zEmptiesEntry, zOptionalText, zPayMethod, zReason, zSaleQty } from './common'
import { zBasketView, zReceiptView } from './views'

const SELL = 'pos.sale.create'

const zVoucherCents = z
  .int({ error: 'Bitte einen Betrag eingeben.' })
  .min(VOUCHER_MIN_CENTS, { error: 'Ein Gutschein lautet auf mindestens 1,00 €.' })
  .max(VOUCHER_MAX_CENTS, { error: 'Ein Gutschein lautet auf höchstens 500,00 €.' })

const zMinAge = z.union([z.literal(16), z.literal(18)], { error: 'Bitte bestätigen Sie das geprüfte Mindestalter (16 oder 18).' })

/** Ein Schritt am laufenden Bon. Zeilen werden über ihre `lineId` aus der letzten Antwort angesprochen. */
export const zBasketOp = z.discriminatedUnion('op', [
  /** Barcode eines Gebindes: legt eine Zeile an oder zählt die vorhandene hoch */
  z.strictObject({ op: z.literal('scan'), code: z.string().trim().min(1, { error: 'Bitte einen Barcode eingeben.' }).max(80), qty: zSaleQty.default(1000) }),
  z.strictObject({ op: z.literal('add'), itemId: zId, unitId: zId, qty: zSaleQty.default(1000) }),
  z.strictObject({ op: z.literal('setQty'), lineId: zId, qty: zSaleQty }),
  z.strictObject({ op: z.literal('setUnit'), lineId: zId, unitId: zId }),
  z.strictObject({ op: z.literal('setLineDiscount'), lineId: zId, discountBp: zDiscountBp }),
  /** Seriennummern der Stücke einer Position (Artikel mit Seriennummernpflicht) – ersetzt die bisherige Auswahl */
  z.strictObject({ op: z.literal('setSerials'), lineId: zId, serialNos: z.array(z.string().trim().min(1, { error: 'Bitte eine Seriennummer eingeben.' }).max(80)).max(50) }),
  /** Position vom Bon nehmen (Zeilenstorno) */
  z.strictObject({ op: z.literal('remove'), lineId: zId, reason: zOptionalText(300) }),
  /** gezähltes Leergut – ersetzt die bisherige Zählung des Bons; leer = keines */
  z.strictObject({ op: z.literal('setEmpties'), entries: z.array(zEmptiesEntry).max(30) }),
  z.strictObject({ op: z.literal('addVoucher'), amountCents: zVoucherCents }),
  z.strictObject({ op: z.literal('removeVoucher'), lineId: zId }),
  z.strictObject({ op: z.literal('setReceiptDiscount'), discountBp: zDiscountBp }),
  z.strictObject({ op: z.literal('setCustomer'), partnerId: zId.nullable() }),
  /** die Kassenkraft bestätigt: Alter geprüft, mindestens `minAge` */
  z.strictObject({ op: z.literal('confirmAge'), minAge: zMinAge }),
  /** Alter nicht nachgewiesen: alle Artikel mit Altersgrenze kommen vom Bon (vermerkt) */
  z.strictObject({ op: z.literal('refuseAge') }),
])
export type BasketOp = z.input<typeof zBasketOp>

export const saleEdit = defineContract({
  name: 'pos.sale.edit',
  title: 'Bon bearbeiten',
  description: 'Ändert den laufenden Bon einer Kasse: scannen, Menge, Gebinde, Seriennummer, Rabatt, Gutschein, Kunde – und was der Betrieb an Branchenfunktionen führt.',
  llm: { description: 'Bearbeitet den laufenden Bon einer Kasse in Schritten. Nur für den Verkaufsbildschirm der Kasse.', keywords: ['Bon', 'kassieren'] },
  input: z.strictObject({
    registerId: zId,
    /** laufender Bon; ohne Angabe beginnt ein neuer */
    basketId: zId.optional(),
    /** nur für einen neuen Bon: Schulungsbon (Trainingsmodus der Sitzung) – ohne Bestand, ohne Zahlung, ohne Umsatz */
    training: z.boolean().optional(),
    ops: z.array(zBasketOp).min(1, { error: 'Es ist kein Schritt angegeben.' }).max(50),
    /**
     * Geräte-Nachweis – Pflicht an Offline-Kassen (docs/kasse-offline/SPEZIFIKATION.md §14.2); der erste Schritt eines
     * neuen Bons trägt `nextBonId` und `vorgangId` (gibt es den Vorgang schon, liefert der Server den Bon)
     */
    till: zTillProof.optional(),
  }),
  output: z.object({
    basket: zBasketView,
    /** Hinweise der Kasse zu diesem Schritt (Aktion, Alterskontrolle, nicht gefunden) */
    notices: z.array(z.object({ tone: z.enum(['info', 'warning']), kind: z.enum(['age_check', 'age_refused', 'note']), message: z.string() })),
    /** Offline-Kasse: höchste vergebene bzw. reservierte Vorgangsnummer der Kasse */
    till: zTillEcho.optional(),
  }),
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
  // Storno, Rabatt über der Grenze, Alterskontrolle nach einer Ablehnung: Die Aufsicht gibt an der Kasse mit ihrer PIN frei
  secondPerson: true,
  limits: { maxItems: 50, itemCount: (input) => input.ops.length },
})

export const salePark = defineContract({
  name: 'pos.sale.park',
  title: 'Bon parken',
  description: 'Legt den laufenden Bon beiseite; er lässt sich in derselben Schicht wieder aufnehmen.',
  llm: { description: 'Parkt den laufenden Bon einer Kasse. Nur für den Verkaufsbildschirm der Kasse.', keywords: ['Bon parken'] },
  input: z.strictObject({ basketId: zId, note: zOptionalText(80) }),
  output: z.object({ id: zId, message: z.string() }),
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const saleResume = defineContract({
  name: 'pos.sale.resume',
  title: 'Geparkten Bon aufnehmen',
  description: 'Nimmt einen geparkten Bon wieder auf.',
  llm: { description: 'Nimmt einen geparkten Bon einer Kasse wieder auf. Nur für den Verkaufsbildschirm der Kasse.', keywords: ['Bon aufnehmen'] },
  input: z.strictObject({ basketId: zId }),
  output: z.object({ basket: zBasketView }),
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const saleVoid = defineContract({
  name: 'pos.sale.void',
  title: 'Bon abbrechen',
  description: 'Bricht den laufenden Bon vor der Zahlung ab. Der Abbruch wird mit Grund vermerkt.',
  llm: { description: 'Bricht einen noch nicht bezahlten Bon ab und vermerkt das. Nur für den Verkaufsbildschirm der Kasse.', keywords: ['Bon abbrechen', 'Bonstorno'] },
  input: z.strictObject({ basketId: zId, reason: zReason, /** Pflicht an Offline-Kassen, ohne nextBonId */ till: zTillProof.optional() }),
  output: z.object({ id: zId, voidedCents: z.int(), message: z.string(), till: zTillEcho.optional() }),
  permission: 'pos.void.create',
  risk: 'destructive',
  scope: 'record',
  assistant: 'never',
  secondPerson: true,
})

export const zPaymentInput = z.strictObject({
  method: zPayMethod,
  /** Teilbetrag in Cent; bei einem Bon mit Auszahlung (Leergut übersteigt den Einkauf) negativ und bar */
  amountCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(-10_000_000).max(10_000_000),
  /** nur bar: was der Kunde gegeben hat – daraus ergibt sich das Rückgeld */
  givenCents: zAmountCentsOrZero.nullish(),
  /** nur Gutschein: der Code */
  voucherCode: zOptionalText(40),
  /** nur Karte: Belegnummer des externen Terminals (optional) */
  reference: zOptionalText(40),
})

export const saleComplete = defineContract({
  name: 'pos.sale.complete',
  title: 'Bon kassieren',
  description: 'Schließt den Bon ab: Zahlungen buchen, Bon festschreiben, Bestand am Markt abbuchen.',
  llm: { description: 'Kassiert den laufenden Bon (bar, Karte am externen Terminal, Gutschein; auch geteilt). Nur für den Verkaufsbildschirm der Kasse.', keywords: ['kassieren', 'bezahlen'] },
  input: z.strictObject({
    basketId: zId,
    /** Betrag, den die Kasse angezeigt hat – weicht er vom Bon ab (Preis geändert), wird nichts gebucht */
    expectedTotalCents: z.int({ error: 'Der angezeigte Betrag fehlt.' }),
    payments: z.array(zPaymentInput).max(6, { error: 'Ein Bon hat höchstens sechs Teilzahlungen.' }).default([]),
    /** Pflicht an Offline-Kassen, ohne nextBonId */
    till: zTillProof.optional(),
  }),
  output: z.object({
    receipt: zReceiptView,
    changeCents: z.int(),
    /** true: Der Bon war bereits bezahlt (doppelt getippt) – es wurde nichts noch einmal gebucht */
    alreadyPaid: z.boolean(),
    message: z.string(),
    till: zTillEcho.optional(),
  }),
  permission: SELL,
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
})
